import { useState } from 'react'
import { Button } from '@shared/ui/Button'
import { generateLinkTelegram } from '@features/link-telegram/api/linkTelegramApi'
import { useTelegramLinkPolling } from '@features/link-telegram/model/useLinkTelegramPolling'
import { usePlatform } from '@shared/platform'
import styles from './OfferTelegramStep.module.css'

interface OfferTelegramStepProps {
  /** Пользователь пропустил связку */
  onSkip: () => void | Promise<void>
  /** Связка Telegram успешно выполнена */
  onLinked: () => void | Promise<void>
}

/**
 * Экран онбординга: предложение связать Telegram после регистрации по email.
 * Открывает ссылку бота и поллит профиль, пока связка не подтвердится.
 * @param onSkip - Колбэк при нажатии «Пропустить»
 * @param onLinked - Колбэк при успешной связке
 */
export const OfferTelegramStep = ({ onSkip, onLinked }: OfferTelegramStepProps) => {
  const platform = usePlatform()
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useTelegramLinkPolling(waiting, async () => {
    setWaiting(false)
    await onLinked()
  })

  const handleLink = async () => {
    setError(null)
    try {
      const { link } = await generateLinkTelegram()
      platform.openLink(link)
      setWaiting(true)
    } catch {
      setError('Не удалось сгенерировать ссылку. Попробуйте позже.')
    }
  }

  const handleSkip = () => {
    setWaiting(false)
    void onSkip()
  }

  return (
    <div className={styles.step}>
      <h1 className={styles.title}>Свяжите Telegram</h1>
      <p className={styles.text}>
        Добавьте Telegram, чтобы пользоваться приложением и в мессенджере, и в браузере.
      </p>

      {waiting ? (
        <>
          <div className={styles.waiting}>Ждём подтверждения от Telegram…</div>
          <Button variant="secondary" onClick={handleSkip} block>Пропустить</Button>
        </>
      ) : (
        <>
          {error && <div className={styles.error}>{error}</div>}
          <Button onClick={handleLink} block>Связать Telegram</Button>
          <Button variant="link" onClick={handleSkip} block>Пропустить</Button>
        </>
      )}
    </div>
  )
}
